import {
	useEffect,
	useState,
} from 'react';
import {
	useNavigate,
} from 'react-router-dom';
import {
	database,
	SettingKey,
	updateSettings,
} from 'utils/database';
import {
	useBasisService,
} from 'services';
import {
	ContentViews,
} from 'utils/navigation/Navigation';
import {
	SessionToken, Setting,
} from 'models';
import {
	RequestTransformer,
} from 'utils/http';
import {
	useInterval,
} from 'hooks';
import {
	toastInfo,
	toastError,
} from 'utils/toast/toast';
import LogRocket from 'logrocket';
import {
	setSessionIdExpires,
} from '../utils/logrocket';
import {
	showToastIfNotInstalled,
} from '../utils/install/utils';

/**
 * Returning bool for if you are authenticated or not.
 * @param token
 */
export const isAuthenticated = (token?: SessionToken): boolean => {
	const {
		expiresOn,
		accessToken,
	} = token || {
	};
	if (accessToken && expiresOn) {
		const isExpired = expiresOn ? expiresOn < +new Date() : true;
		const isTokenValid = accessToken !== undefined && accessToken.length > 0;
		if (isExpired && token) {
			database.settings.delete(SettingKey.SESSION)
				.catch(console.error);
		}

		return isTokenValid && !isExpired;
	}
	return false;
};

export const useAuthService = () => {
	const navigate = useNavigate();
	const {
		data,
		loading: [isLoading, setIsLoading],
		error: [error, setError],
		http,
		table,
		baseUrl,
	} = useBasisService<Setting<SessionToken>>(
		database.settings,
		(table) => table.get(SettingKey.SESSION)
	);
	// TODO: Update for local development and deployment
	// The drivers phone number with area code
	const [number, setNumber] = useState<string>();
	const [isNumberValid, setIsNumberValid] = useState<boolean>(false);
	// The verification code from the backend
	const [verificationCode, setVerificationCode] = useState<string>();
	const [isVerificationCodeValid, setIsVerificationCodeValid] = useState<boolean>();
	const [isWaitingForVerification, setIsWaitingForVerification] = useState<boolean>();
	const [smsCodeRequestTime, setSmsCodeRequestTime] = useState<number>(0);
	const [timeLeftForNewRequest, setTimeLeftForNewRequest] = useState<number>(0);
	const [isUserAuthenticated, setIsUserAuthenticated] = useState(isAuthenticated(data?.value));

	const getTimeLeftForNewRequest = (): number => {
		const timeForNewRequestAvailability = smsCodeRequestTime + 1000 * 60;
		const timeLeft = Math.round((timeForNewRequestAvailability - +new Date()) / 1000);
		return timeLeft > 0 ? timeLeft : 0;
	};

	/**
   * A function for getting the auth token via the verification code
   * TODO: Remember to update to the correct API url for this call
   * @param verificationCode - The verification code that the user received via sms
   */
	const getToken = () => {
		if (!number || !verificationCode || isLoading) {
			return;
		}

		setIsLoading(true);
		const transformer = (response: SessionToken): Setting<SessionToken> =>
			({
				key: SettingKey.SESSION,
				value: {
					...response,
				},
			});

		http.put<any>('token', {
			number,
			password: verificationCode,
		}, {
			baseUrl,
			table,
			transformer: transformer as RequestTransformer,
		}, false)
			.then((res: Setting<SessionToken>) => {
				updateSettings(SettingKey.PHONE, number); // Kan/bør denne legges innunder transformer?
				setIsUserAuthenticated(isAuthenticated(data?.value));
				setIsLoading(false);
				navigate(ContentViews.TRANSPORT.path);
				const expiresInHours = (res.value?.expiresIn || 0) / 60 / 60;
				toastInfo('Velkommen til TakeCargo!', `Sesjonen din går ut om ${expiresInHours} timer.`);
			})
			.catch(error => {
				let message;
				let title;

				if (error.stack) {
					title = 'Tilkoblingsproblemer';
					message = 'Er Internett tilgjengelig? Vi sliter med å kommunisere med sentrale systemer ...';
				}

				else {
					title = 'Feil engangskode';
					message = 'Vennligst forsøk igjen ...';
				}

				setIsLoading(false);
				toastError(title, message);
				setError(error);
			});
	};

	/**
   * A function for triggering the verification code sms
   * The backend will check if the phone number is in the database
   * and send a code if they do
   */
	const getCodeForNumber = (): Promise<void> => {
		return new Promise<any>((resolve, reject) => {
			if (!number) {
				reject('Mangler telefonnummer');
				return;
			}

			if (isLoading) {
				reject('Lasting allerede i gang ...');
				return;
			}

			setIsLoading(true);
			http.post('token/otp', {
				number,
			}, {
				baseUrl,
				discardResponse: true,
			})
				.then((result) => {
					setIsWaitingForVerification(true);
					setIsLoading(false);
					setNumber(number);
					setSmsCodeRequestTime(+new Date());
					resolve(result);
				})
				.catch(error => {
					let message;
					let title;
					switch (error.status) {
						case 400:
							title = 'Ugyldig nummer';
							message = 'Telefonnummeret du tastet inn er ikke gyldig';
							break;

						case 403:
							title = 'Mangler transport';
							message = 'Ingen transport er tilknyttet dette telefonnummeret';
							break;

						case 429:
							title = 'For mange påloggings forsøk';
							message = 'Vennligst vent 1 minutt, og forsøk igjen';
							break;

						case 477:
							title = 'Ingen transportoppdrag';
							message = 'Det fins ikke transportoppdrag på ditt mobiltelefonummer innenfor siste eller neste måned.';
							break;

						case 478:
							title = 'Ugyldig telefonnummer';
							message = 'Det ser ut til at telefonnummeret har feil format. Vennligst sjekk og prøv igjen.';
							break;

						default:
							if (error.stack) {
								title = 'Tilkoblingsproblemer';
								message = 'Er Internett tilgjengelig? Vi sliter med å kommunisere med sentrale systemer ...';
							}
							else {
								title = 'Ukjent problem';
								message = 'Teknisk problem: vi klarer ikke å sende engangskode';
							}

							LogRocket.error('useAuthService.getCodeForNumber', error);
					}

					setIsWaitingForVerification(false);
					setError(error);
					toastError(title, message);
					setIsLoading(false);
					reject(error);
				});
		});
	}

	const logout = () => {
		table.delete(SettingKey.SESSION)
			.catch(console.error)
			.finally(() => {
				setSessionIdExpires(+new Date());
				navigate(ContentViews.LOGIN.path);
				showToastIfNotInstalled();
			});
	};

	useInterval(() => {
		if (isWaitingForVerification) {
			setTimeLeftForNewRequest(getTimeLeftForNewRequest());
		}
		setIsUserAuthenticated(isAuthenticated(data?.value));
	}, 250);

	useEffect(() => {
		if (isUserAuthenticated && data?.value?.expiresOn) {
			setSessionIdExpires(data?.value?.expiresOn);
		}
	}, [isUserAuthenticated, data?.value?.expiresOn]);

	return {
		jwt: data?.value,
		isLoading,
		error,
		number, setNumber,
		isNumberValid, setIsNumberValid,
		verificationCode, setVerificationCode,
		isVerificationCodeValid, setIsVerificationCodeValid,
		timeLeftForNewRequest,
		getToken,
		getCodeForNumber,
		logout,
		isAuthenticated: isUserAuthenticated,
		isWaitingForVerification, setIsWaitingForVerification,
	};
};